import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, Col, Row } from "reactstrap";

import ProfessionalAvatar from "../../../Components/Common/ProfessionalAvatar";
import BookingCatalogSkeleton from "./BookingCatalogSkeleton";
import {
  PublicBookingProfessional,
  PublicBookingService,
} from "../../../services/publicBookingService";

interface ProfessionalStepProps {
  service: PublicBookingService;
  professionals: PublicBookingProfessional[];
  loading: boolean;
  selectedProfessional: PublicBookingProfessional | null;
  onSelectProfessional: (professional: PublicBookingProfessional) => void;
  onBack: () => void;
}

const ProfessionalStep: React.FC<ProfessionalStepProps> = ({
  service,
  professionals,
  loading,
  selectedProfessional,
  onSelectProfessional,
  onBack,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div>
          <p className="text-muted mb-1">{t("public_booking.professionals.selectedService")}</p>
          <h5 className="mb-0">{service.name}</h5>
        </div>
        <Button color="light" size="sm" onClick={onBack}>
          <i className="ri-arrow-left-line align-bottom me-1" />
          {t("public_booking.common.back")}
        </Button>
      </div>

      {loading ? (
        <>
          <p className="text-muted mb-3">{t("public_booking.professionals.loading")}</p>
          <BookingCatalogSkeleton count={2} />
        </>
      ) : professionals.length === 0 ? (
        <div className="text-center py-5">
          <div className="avatar-md mx-auto mb-3">
            <span className="avatar-title rounded-circle bg-light text-muted fs-24">
              <i className="ri-user-smile-line" />
            </span>
          </div>
          <h5 className="mb-2">{t("public_booking.professionals.emptyTitle")}</h5>
          <p className="text-muted mb-0">{t("public_booking.professionals.emptyDescription")}</p>
        </div>
      ) : (
        <Row className="g-3">
          {professionals.map((professional) => {
            const isSelected = selectedProfessional?.id === professional.id;

            return (
              <Col xs={12} key={professional.id}>
                <Card
                  className={`border shadow-none mb-0${isSelected ? " border-primary border-2" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectProfessional(professional)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectProfessional(professional);
                    }
                  }}
                >
                  <CardBody className="d-flex align-items-center gap-3">
                    <ProfessionalAvatar
                      name={professional.name}
                      colorHex={professional.color_hex}
                      photoUrl={professional.photo_url}
                      size={56}
                    />
                    <div className="flex-grow-1 min-w-0">
                      <h5 className="fs-16 mb-0">{professional.name}</h5>
                    </div>
                    <Button
                      color={isSelected ? "primary" : "soft-primary"}
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelectProfessional(professional);
                      }}
                    >
                      {isSelected
                        ? t("public_booking.common.selected")
                        : t("public_booking.common.select")}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </>
  );
};

export default ProfessionalStep;
